import {
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button,
  FormControl,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useState } from 'react';
import { useUserStore } from '@/state/stores/use-user-store';
import { useDeleteUserMutation } from '@/state/mutations/users/use-delete-user';
import { useNotifications } from '@/hooks/use-notifications';
import { useAuth, useUser } from '@clerk/clerk-react';
import { accordionSummaryStyles } from './general-tab.constants';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useAccountDeletionImpactQuery } from '@/state/queries/users/use-account-deletion-impact';

type DeletionStep = 'initial' | 'confirm';

export const DeleteUserForm = () => {
  const t = useTranslations();
  const notification = useNotifications();
  const { user: clerkUser } = useUser();
  const user = useUserStore((s) => s.user);
  const { signOut } = useAuth();
  const setActiveOrganization = useActiveOrganizationStore((s) => s.setActiveOrganization);
  const { data: accountDeletionImpact } = useAccountDeletionImpactQuery();

  const organizationsWhereSoleOwner = accountDeletionImpact?.organizationsThatWillBeDeleted.map((o) => o.name) ?? [];

  const [deletionStep, setDeletionStep] = useState<DeletionStep>('initial');
  const [verifyFullName, setVerifyFullName] = useState<string>('');
  const { mutateAsync: deleteUserMutation } = useDeleteUserMutation();

  const deleteNameMatches = user?.firstName + ' ' + user?.lastName === verifyFullName;

  const handleDeleteAccount = useCallback(async () => {
    if (deletionStep === 'initial') {
      setDeletionStep('confirm');
    } else {
      if (deletionStep === 'confirm' && deleteNameMatches) {
        try {
          await deleteUserMutation();
          notification.notifySuccess(t('user.profile.deleteAccount.notification.deleteSuccess'));
          setActiveOrganization(undefined);
          signOut();
        } catch {
          notification.notifyError(t('user.profile.deleteAccount.notification.deleteError'));
        }
      }
    }
  }, [deleteNameMatches, deletionStep, deleteUserMutation, notification, t, signOut, setActiveOrganization]);

  const verifyFullNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerifyFullName(event.target.value);
  };

  return (
    <Accordion sx={{ backgroundColor: 'transparent' }} data-testid="delete-user-accordion">
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
        <Typography>{t('user.profile.deleteAccount.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ py: 0 }}>
        <Stack spacing={1}>
          <Typography variant="body2">
            {t('user.profile.deleteAccount.explanation', { email: clerkUser?.primaryEmailAddress?.toString() ?? '' })}
          </Typography>
          {organizationsWhereSoleOwner.length > 0 && (
            <>
              <Typography variant="body2" color="error">
                {t('user.profile.deleteAccount.soleOwner', { organizations: organizationsWhereSoleOwner.join(', ') })}
              </Typography>
              <Typography variant="body2">{t('user.profile.deleteAccount.note')}</Typography>
            </>
          )}
          {deletionStep === 'confirm' && (
            <FormControl variant="standard">
              <Typography variant="caption">{t('user.profile.deleteAccount.deleteProcess')}</Typography>
              <TextField
                value={verifyFullName}
                onChange={verifyFullNameChangeHandler}
                variant="standard"
                type="text"
                sx={{ maxWidth: 200 }}
                autoComplete="off"
                slotProps={{ htmlInput: { 'data-testid': 'delete-user-verification-input' } }}
              />
            </FormControl>
          )}
          <Stack alignItems="flex-start">
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleDeleteAccount}
              disabled={deletionStep === 'confirm' && !deleteNameMatches}
              data-testid="delete-user-button"
            >
              {t('user.profile.deleteAccount.button')}
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
